import type { MovimentoRecord } from "@/lib/movimento";
import { movimentoRiferimenti, movimentoRifiuto } from "@/lib/movimento";

export type MovimentoStatoUi =
  | "bozza"
  | "in_elaborazione"
  | "inviato"
  | "annullato"
  | "errore";

export function movimentoTipoBadge(m: MovimentoRecord): {
  letter: string;
  title: string;
  className: string;
} {
  const rif = movimentoRiferimenti(m);
  const cs = String(rif.causale_operazione_cs ?? "").toLowerCase();
  const causale = String(rif.causale_operazione ?? m.causale ?? "").toUpperCase();
  if (cs.includes("scarico") || causale === "AT" || causale === "A*T") {
    return {
      letter: "S",
      title: "Scarico",
      className: "bg-slate-500 text-white",
    };
  }
  if (cs.includes("rettif") || causale === "RE") {
    return {
      letter: "R",
      title: "Rettifica",
      className: "bg-amber-600 text-white",
    };
  }
  return {
    letter: "C",
    title: "Carico",
    className: "bg-emerald-600 text-white",
  };
}

export function movimentoProgressivo(m: MovimentoRecord): number | null {
  const rif = movimentoRiferimenti(m);
  const num = rif.numero_registrazione as Record<string, unknown> | undefined;
  const p = num?.progressivo ?? rif.progressivo;
  const n = Number(p);
  return Number.isFinite(n) ? n : null;
}

export function movimentoAnno(m: MovimentoRecord): number | null {
  const rif = movimentoRiferimenti(m);
  const num = rif.numero_registrazione as Record<string, unknown> | undefined;
  const a = num?.anno ?? rif.anno;
  if (a) {
    const n = Number(a);
    if (Number.isFinite(n)) return n;
  }
  const raw = String(rif.data_ora_registrazione ?? m.data ?? "");
  const y = parseInt(raw.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
}

export function movimentoEerCode(m: MovimentoRecord): string {
  const r = movimentoRifiuto(m);
  return String(r.codice_eer ?? m.eer ?? "—").replace(/\./g, "");
}

const STATI_UI = new Set<MovimentoStatoUi>([
  "bozza",
  "in_elaborazione",
  "inviato",
  "annullato",
  "errore",
]);

/** Usa stato_ui dal backend enriched se presente. */
export function resolveStatoUi(m: MovimentoRecord): MovimentoStatoUi {
  const api = (m as Record<string, unknown>).stato_ui;
  if (typeof api === "string" && STATI_UI.has(api as MovimentoStatoUi)) {
    return api as MovimentoStatoUi;
  }
  const draftStatus = (m as Record<string, unknown>).draft_status;
  return inferStatoUi(m, typeof draftStatus === "string" ? draftStatus : undefined);
}

export function inferStatoUi(
  m: MovimentoRecord,
  draftStatus?: string,
): MovimentoStatoUi {
  const ann = movimentoRiferimenti(m).annullato;
  if (ann === true || String(ann).toLowerCase() === "true") return "annullato";
  if (draftStatus === "sent_failed") return "errore";
  if (draftStatus === "sent") return "inviato";
  if (draftStatus === "pending") return "in_elaborazione";
  if (draftStatus === "ready") return "in_elaborazione";
  if (m.identificativo_movimento || movimentoProgressivo(m)) return "inviato";
  return "bozza";
}

export function statoUiLabel(s: MovimentoStatoUi): string {
  const map: Record<MovimentoStatoUi, string> = {
    bozza: "Bozza",
    in_elaborazione: "In elaborazione",
    inviato: "Inviato",
    annullato: "Annullato",
    errore: "Errore",
  };
  return map[s];
}

export function statoUiVariant(
  s: MovimentoStatoUi,
): "default" | "secondary" | "outline" | "destructive" {
  if (s === "inviato") return "default";
  if (s === "in_elaborazione") return "secondary";
  if (s === "annullato") return "destructive";
  if (s === "errore") return "destructive";
  return "outline";
}

export function statoUiHasPending(s: MovimentoStatoUi): boolean {
  return s === "in_elaborazione";
}

export function rowsHaveActivity(rows: MovimentoRecord[] | undefined): boolean {
  if (!rows) return false;
  return rows.some((m) => statoUiHasPending(resolveStatoUi(m)));
}

export function rowDraftId(m: MovimentoRecord): string | null {
  const v = (m as Record<string, unknown>).draft_id;
  return typeof v === "string" && v ? v : null;
}

export function rowErrorDetail(m: MovimentoRecord): string | null {
  const v = (m as Record<string, unknown>).error_detail;
  return typeof v === "string" && v ? humanizeRentriError(v) : null;
}

/** Messaggio leggibile dai campi in errore (`model_state`) di una risposta RENTRI 400. */
export function rentriModelStateMessage(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const ms = (body as Record<string, unknown>).model_state;
  if (!ms || typeof ms !== "object") return null;
  const parts = Object.entries(ms as Record<string, unknown>).map(([k, v]) =>
    Array.isArray(v) && v.length ? `${k}: ${String(v[0])}` : k,
  );
  return parts.length ? humanizeRentriError(parts.join("; ")) : null;
}

const RENTRI_CODICI: Record<string, string> = {
  "sys.required": "obbligatorio",
  "sys.invalid": "non valido",
};

/** "[0].destinatario.num_autorizzazione: sys.required" → "destinatario › num autorizzazione: obbligatorio" */
export function humanizeRentriError(detail: string): string {
  return detail
    .split(";")
    .map((part) => {
      const [campo, ...rest] = part.trim().split(":");
      const codice = rest.join(":").trim();
      const label = campo
        .replace(/^\[\d+\]\.?/, "")
        .split(".")
        .map((s) => s.replace(/_/g, " "))
        .join(" › ");
      if (!codice) return label;
      return `${label}: ${RENTRI_CODICI[codice] ?? codice}`;
    })
    .filter(Boolean)
    .join("; ");
}

export function rowJobAttempts(m: MovimentoRecord): number | null {
  const v = (m as Record<string, unknown>).job_attempts;
  return typeof v === "number" ? v : null;
}

export function rowIsVirtual(m: MovimentoRecord): boolean {
  return Boolean((m as Record<string, unknown>)._virtual);
}
